import type { QualityLevel } from '../core/quality';

export const PALETTE = [
  '#d7263d',
  '#ff7b2f',
  '#ffb627',
  '#6ab547',
  '#17bebb',
  '#4cc9f0',
  '#2667ff',
  '#7d5ba6',
  '#e84a9a',
  '#6b4226',
  '#22242a',
  '#f4f4f6',
] as const;

export interface HudCallbacks {
  onColorChange(color: string): void;
  onSizeChange(size: number): void;
  onSnowfallChange(value: number): void;
  onReset(): void;
  onCameraToggle(): void;
  onLocations(): void;
  onPhoto(): void;
  onHelp(): void;
  onMuteToggle(): void;
  onQualityChange(level: QualityLevel): void;
}

/** The in-game HUD: palette + custom picker, spray size, snowfall intensity,
 * and the button row. Collapsible and hidden entirely by photo mode. */
export class Hud {
  readonly root: HTMLDivElement;
  private reticle: HTMLDivElement;
  private toastEl: HTMLDivElement;
  private hintEl: HTMLDivElement;
  private swatches: HTMLButtonElement[] = [];
  private colorInput!: HTMLInputElement;
  private hexInput!: HTMLInputElement;
  private snowSlider!: HTMLInputElement;
  private muteBtn!: HTMLButtonElement;
  private cameraBtn!: HTMLButtonElement;
  private toastTimer = 0;

  constructor(
    parent: HTMLElement,
    private readonly cb: HudCallbacks,
    initial: {
      color: string;
      size: number;
      snowfall: number;
      quality: QualityLevel;
      muted: boolean;
    },
  ) {
    this.root = document.createElement('div');
    this.root.id = 'hud';
    this.root.innerHTML = `
      <div class="hud-panel">
        <div class="palette"></div>
        <div class="custom-row">
          <input type="color" title="Custom color" />
          <input type="text" maxlength="7" spellcheck="false" title="Hex color" />
        </div>
        <div class="hud-row"><span>Spray size</span><input type="range" id="hud-size" min="0" max="1" step="0.01" /></div>
        <div class="hud-row"><span>Snowfall</span><input type="range" id="hud-snow" min="0" max="1" step="0.01" /></div>
        <div class="hud-row"><span>Quality</span>
          <select id="hud-quality">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div class="hud-buttons">
          <button id="hud-camera" title="Toggle camera (V)">Camera</button>
          <button id="hud-photo" title="Photo mode (P)">Photo</button>
          <button id="hud-locations">Locations</button>
          <button id="hud-help" title="Help (H)">Help</button>
          <button id="hud-mute" title="Mute (M)">Sound</button>
          <button id="hud-reset" title="Clear all paint and snow">Reset</button>
        </div>
      </div>
      <button id="hud-collapse" title="Collapse HUD (Tab)">☰ HUD</button>
    `;
    parent.appendChild(this.root);

    const palette = this.root.querySelector('.palette')!;
    PALETTE.forEach((color, i) => {
      const btn = document.createElement('button');
      btn.className = 'swatch';
      btn.style.background = color;
      btn.title = i < 9 ? `${color} (key ${i + 1})` : color;
      if (i < 9) btn.innerHTML = `<span class="num">${i + 1}</span>`;
      btn.addEventListener('click', () => this.pickColor(color));
      palette.appendChild(btn);
      this.swatches.push(btn);
    });

    this.colorInput = this.root.querySelector('input[type=color]')!;
    this.hexInput = this.root.querySelector('input[type=text]')!;
    this.colorInput.addEventListener('input', () => this.pickColor(this.colorInput.value));
    this.hexInput.addEventListener('change', () => {
      let v = this.hexInput.value.trim();
      if (!v.startsWith('#')) v = '#' + v;
      if (/^#[0-9a-fA-F]{6}$/.test(v)) this.pickColor(v.toLowerCase());
      else this.hexInput.value = this.colorInput.value;
    });

    const sizeSlider = this.root.querySelector<HTMLInputElement>('#hud-size')!;
    sizeSlider.value = String(initial.size);
    sizeSlider.addEventListener('input', () => this.cb.onSizeChange(Number(sizeSlider.value)));
    this.sizeSlider = sizeSlider;

    this.snowSlider = this.root.querySelector<HTMLInputElement>('#hud-snow')!;
    this.snowSlider.value = String(initial.snowfall);
    this.snowSlider.addEventListener('input', () =>
      this.cb.onSnowfallChange(Number(this.snowSlider.value)),
    );

    const quality = this.root.querySelector<HTMLSelectElement>('#hud-quality')!;
    quality.value = initial.quality;
    quality.addEventListener('change', () =>
      this.cb.onQualityChange(quality.value as QualityLevel),
    );

    const on = (id: string, fn: () => void) =>
      this.root.querySelector<HTMLButtonElement>(`#${id}`)!.addEventListener('click', fn);
    on('hud-camera', () => this.cb.onCameraToggle());
    on('hud-photo', () => this.cb.onPhoto());
    on('hud-locations', () => this.cb.onLocations());
    on('hud-help', () => this.cb.onHelp());
    on('hud-mute', () => this.cb.onMuteToggle());
    on('hud-reset', () => this.cb.onReset());
    on('hud-collapse', () => this.root.classList.toggle('collapsed'));

    this.muteBtn = this.root.querySelector('#hud-mute')!;
    this.cameraBtn = this.root.querySelector('#hud-camera')!;
    this.setMuted(initial.muted);
    this.setColor(initial.color);

    this.reticle = document.createElement('div');
    this.reticle.id = 'reticle';
    parent.appendChild(this.reticle);

    this.toastEl = document.createElement('div');
    this.toastEl.id = 'toast';
    parent.appendChild(this.toastEl);

    this.hintEl = document.createElement('div');
    this.hintEl.id = 'hint';
    this.hintEl.classList.add('hidden');
    parent.appendChild(this.hintEl);
  }

  private sizeSlider!: HTMLInputElement;

  private pickColor(color: string): void {
    this.setColor(color);
    this.cb.onColorChange(color);
  }

  setColor(color: string): void {
    this.colorInput.value = color;
    this.hexInput.value = color;
    PALETTE.forEach((c, i) => this.swatches[i]!.classList.toggle('selected', c === color));
  }

  setSize(size: number): void {
    this.sizeSlider.value = String(size);
  }

  setSnowfall(value: number): void {
    this.snowSlider.value = String(value);
  }

  setMuted(muted: boolean): void {
    this.muteBtn.textContent = muted ? 'Unmute' : 'Mute';
  }

  setCameraMode(mode: 'first' | 'third'): void {
    this.cameraBtn.textContent = mode === 'first' ? '3rd person' : '1st person';
  }

  setReticlePaintable(paintable: boolean): void {
    this.reticle.classList.toggle('paintable', paintable);
  }

  /** Hide/show the whole HUD chrome (photo mode, menus). */
  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
    this.reticle.classList.toggle('hidden', !visible);
  }

  showHint(text: string | null): void {
    if (text) {
      this.hintEl.textContent = text;
      this.hintEl.classList.remove('hidden');
    } else {
      this.hintEl.classList.add('hidden');
    }
  }

  toast(message: string): void {
    this.toastEl.textContent = message;
    this.toastEl.classList.add('show');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('show'), 2200);
  }
}
